import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, Plus, Minus, Moon, Droplet, Smile } from "lucide-react";
import { api } from "../api/client";

const moodLabels = ["", "Bardzo zle", "Slabo", "OK", "Dobrze", "Swietnie"];

const metrics = [
  { key: "sleep", label: "Sen", color: "#667EEA", icon: "moon", unit: "h", min: 0, max: 12, step: 0.5 },
  { key: "mood", label: "Nastroj", color: "#FBBF24", icon: "smile", unit: "/5", min: 1, max: 5, step: 1 },
  { key: "hydration", label: "Woda", color: "#4ECDC4", icon: "droplet", unit: "L", min: 0, max: 5, step: 0.1 },
];

const HealthCheckWidget = ({ token }) => {
  const [data, setData] = useState(null);
  const [sleep, setSleep] = useState(7);
  const [mood, setMood] = useState(3);
  const [hydration, setHydration] = useState(1.5);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchToday();
  }, []);

  const fetchToday = async () => {
    try {
      const res = await api("get", "/health-checks/today", null, token);
      if (res.data) {
        setData(res.data);
        setSleep(res.data.sleep_hours);
        setMood(res.data.mood);
        setHydration(res.data.hydration_liters);
        setSaved(true);
      }
    } catch (err) {
      // No entry yet
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      const res = await api("post", "/health-checks", {
        sleep_hours: sleep,
        mood: mood,
        hydration_liters: hydration
      }, token);
      setData(res.data);
      setSaved(true);
      toast.success("Health check zapisany");
    } catch (err) {
      toast.error("Blad zapisu");
    }
  };

  const values = { sleep, mood, hydration };
  const setters = { sleep: setSleep, mood: setMood, hydration: setHydration };

  const adjust = (key, delta) => {
    const m = metrics.find(x => x.key === key);
    setters[key](prev => {
      const next = Math.round((prev + delta) * 10) / 10;
      if (next < m.min) return m.min;
      if (next > m.max) return m.max;
      setSaved(false);
      return next;
    });
  };

  if (loading) return null;

  const iconMap = {
    moon: <Moon size={20} />,
    smile: <Smile size={20} />,
    droplet: <Droplet size={20} />,
  };

  const displayValue = (key, val) => {
    if (key === "hydration") return val.toFixed(1);
    return val;
  };

  return (
    <div className="hc-horizontal" data-testid="health-check-widget">
      <h2 className="hc-title">DZISIEJSZY CHECK-IN</h2>
      <div className="hc-cards-row">
        {metrics.map(mt => {
          const val = values[mt.key];
          return (
            <div key={mt.key} className="hc-card">
              <div className="hc-card-icon" style={{ backgroundColor: mt.color + '20', color: mt.color }}>
                {iconMap[mt.icon]}
              </div>
              <span className="hc-card-label">{mt.label.toUpperCase()}</span>
              <div className="hc-card-value-row">
                <span className="hc-card-value">{displayValue(mt.key, val)}</span>
                <span className="hc-card-unit">{mt.unit}</span>
              </div>
              {mt.key === "mood" && <span className="hc-card-mood-label">{moodLabels[val]}</span>}
              <div className="hc-card-controls">
                <button className="hc-adjust-btn" onClick={() => adjust(mt.key, -mt.step)}>
                  <Minus size={14} />
                </button>
                <button className="hc-adjust-btn" onClick={() => adjust(mt.key, mt.step)}>
                  <Plus size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <Button
        onClick={save}
        disabled={saved}
        className="hc-save-full"
        size="sm"
      >
        {saved ? <><Check size={14} /> Zapisano</> : "Zapisz Check-in"}
      </Button>
    </div>
  );
};

export default HealthCheckWidget;
